import CoverImage from '@/assets/images/png/landing-page.png';

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

/** Max length of the image file name — matches the backend
 *  LandingPageBranding.imageFileName column (nvarchar(50)). */
export const MAX_IMAGE_NAME_LENGTH = 50;

/** Max lengths of the text fields. buildPayload mirrors these into the
 *  brandingInformation columns they must fit: caption -> titleOne (50),
 *  headline -> titleTwo (200), paragraph -> platformSummary (500). */
export const MAX_CAPTION_LENGTH = 50;
export const MAX_HEADLINE_LENGTH = 200;
export const MAX_PARAGRAPH_LENGTH = 500;

export type TextAlign = 'left' | 'center' | 'right';
export type ImagePosition = 'full' | 'left' | 'right' | 'background';
export type ContentPosition = 'left' | 'center' | 'right' | 'top' | 'bottom';
export type BoxEffect = 'none' | 'glass' | 'shadow' | 'gradient';

/** A file the user just picked, or metadata for one already stored by the API.
 *  `key` is the storage key the backend needs to re-reference an existing file
 *  on update — it MUST survive the edit round-trip. */
export type LandingImage =
  | File
  | {
      fileName: string;
      internalName?: string;
      filePath?: string;
      key?: string;
    }
  | null;

/** The single, flat configuration that drives the login/landing page. */
export interface LandingConfig {
  // text content
  caption: string;
  headline: string;
  paragraph: string;
  textAlign: TextAlign;
  captionColor: string;
  headlineColor: string;
  paragraphColor: string;
  // CTA buttons
  primaryEnabled: boolean;
  primaryLabel: string;
  primaryBtnColor: string;
  secondaryEnabled: boolean;
  secondaryLabel: string;
  secondaryBtnColor: string;
  /** Link the secondary button opens. https-only — the landing page is
   *  public, so plain-http targets are rejected by validation. */
  secondaryUrl: string;
  // content box
  boxEnabled: boolean;
  boxColor: string;
  boxEffect: BoxEffect;
  boxOpacity: number;
  boxShadow: number;
  boxBlur: number;
  // image — `image` holds the new File / existing metadata used for upload;
  // `imageUrl` is a preview-only source (blob URL or pasted URL).
  image: LandingImage;
  imageUrl: string;
  imageOverlay: boolean;
  imageOverlayColor: string;
  imageOverlayOpacity: number;
  // layout
  imagePosition: ImagePosition;
  contentPosition: ContentPosition;
  borderRadius: number;
  contentPadding: number;
  contentWidth: number;
}

/* -------------------------------------------------------------------------- */
/*  Defaults                                                                   */
/* -------------------------------------------------------------------------- */

const DEFAULT_PARAGRAPH =
  'Shares.Vision is an integrated software solution that support companies ' +
  'in managing their "Long Term Incentive Plan" (LTIP) whether in the form ' +
  'of equity or cash plans. The solution aims to provide both HR and ' +
  'employees easy access to an incentive plan.';

export const DEFAULT_LANDING_CONFIG: LandingConfig = {
  caption: 'Shares.Vision',
  headline: 'Streamline Your Employee Share Plans with Shares.Vision',
  paragraph: DEFAULT_PARAGRAPH,
  textAlign: 'left',
  captionColor: '#3b82f6',
  headlineColor: '#111111',
  paragraphColor: '#555555',
  primaryEnabled: true,
  primaryLabel: 'Login',
  primaryBtnColor: '#3b82f6',
  secondaryEnabled: true,
  secondaryLabel: 'Know More',
  secondaryBtnColor: '#111111',
  secondaryUrl: '',
  boxEnabled: false,
  boxColor: '#f3f4f6',
  boxEffect: 'none',
  boxOpacity: 100,
  boxShadow: 18,
  boxBlur: 8,
  image: null,
  imageUrl: CoverImage,
  imageOverlay: false,
  imageOverlayColor: '#000000',
  imageOverlayOpacity: 40,
  imagePosition: 'right',
  contentPosition: 'left',
  borderRadius: 12,
  contentPadding: 48,
  contentWidth: 600,
};

/* -------------------------------------------------------------------------- */
/*  Backend mapping                                                            */
/*  The API stores enum IDs (numbers), not the UI's string values. These maps  */
/*  come from the backend lookup tables.                                       */
/* -------------------------------------------------------------------------- */

const TEXT_ALIGNMENT_ID: Record<TextAlign, number> = {
  left: 1,
  center: 2,
  right: 3,
};

const CONTENT_BOX_EFFECT_ID: Record<BoxEffect, number> = {
  none: 1,
  glass: 2,
  shadow: 3,
  gradient: 4,
};

const IMAGE_POSITION_ID: Record<ImagePosition, number> = {
  full: 1, // "Fullwidth"
  left: 2,
  right: 3,
  background: 4,
};

const CONTENT_POSITION_ID: Record<ContentPosition, number> = {
  left: 1,
  center: 2,
  right: 3,
  top: 4,
  bottom: 5,
};

/** Serialize the scalar UI config into the backend payload shape.
 *  Image binary/metadata is appended separately in buildPayload. */
export const toLandingBackendPayload = (c: LandingConfig) => {
  // Content Position only applies to the overlay layouts (Full Width /
  // Background).
  const isOverlayLayout =
    c.imagePosition === 'full' || c.imagePosition === 'background';

  return {
    caption: c.caption,
    headline: c.headline,
    paragraph: c.paragraph,

    textAlignmentId: TEXT_ALIGNMENT_ID[c.textAlign],

    captionColor: c.captionColor,
    headlineColor: c.headlineColor,
    paragraphColor: c.paragraphColor,

    isPrimaryCTAEnabled: c.primaryEnabled,
    primaryCTAText: c.primaryLabel,
    primaryCTAButtonColor: c.primaryBtnColor,

    isSecondaryCTAEnabled: c.secondaryEnabled,
    // A disabled CTA posts its text and URL cleared. The URL goes out as ''
    // rather than null so the key stays in the form data (buildPayload omits
    // null keys) — multipart cannot carry a literal null, and the backend
    // binds the empty field to null.
    secondaryCTAText: c.secondaryEnabled ? c.secondaryLabel : '',
    secondaryCTAButtonColor: c.secondaryBtnColor,
    secondaryCTAUrl: (c.secondaryEnabled && c.secondaryUrl?.trim()) || '',

    // When the content box is disabled, all its nested fields go null.
    isContentBoxBackgroundEnabled: c.boxEnabled,
    contentBoxBackgroundColor: c.boxEnabled
      ? c.boxColor?.trim() || DEFAULT_LANDING_CONFIG.boxColor
      : null,
    contentBoxOpacity: c.boxEnabled ? c.boxOpacity : null,
    contentBoxEffectId: c.boxEnabled
      ? CONTENT_BOX_EFFECT_ID[c.boxEffect]
      : null,
    contentBoxBlur: c.boxEnabled && c.boxEffect === 'glass' ? c.boxBlur : null,
    contentBoxShadow:
      c.boxEnabled && c.boxEffect === 'shadow' ? c.boxShadow : null,

    isImageOverlayEnabled: c.imageOverlay,
    imageOverlayColor: c.imageOverlay ? c.imageOverlayColor : null,
    imageOverlayOpacity: c.imageOverlay ? c.imageOverlayOpacity : null,

    layoutImagePositionId: IMAGE_POSITION_ID[c.imagePosition],
    // Content Position must be 0 for the Left/Right split layouts. Every other
    // shape was tried against the API (16 Jul 2026) and rejected up front:
    //   1-5      -> 400 "Content Position must be empty (0) when Image
    //                    Position is Left or Right"
    //   null     -> 400, same message (the DTO field is nullable, so an omitted
    //               key binds to null — which the validator does NOT treat as
    //               "empty"; it wants the literal 0)
    // 0 is therefore the only payload the contract accepts, and saving a
    // Left/Right layout still returns 500 ERR004 with it. That failure is
    // server-side and unfixable from here. Its cause is NOT confirmed — the
    // "ContentPosition FK rejects 0" story is a hypothesis, never checked
    // against server logs. Trace the 500 by traceId before assuming it.
    layoutContentPositionId: isOverlayLayout
      ? CONTENT_POSITION_ID[c.contentPosition]
      : 0,
    layoutBorderRadius: c.borderRadius,
    layoutContentPadding: c.contentPadding,
    layoutContentWidth: c.contentWidth,
  };
};

/** Resolve an enum value to its UI key, accepting either the numeric ID or the
 *  string name the API returns (e.g. 4 or "Background" → 'background'). The map
 *  keys ARE the UI keys, so a case-insensitive name match works too; `aliases`
 *  covers names that differ from the key (e.g. "Fullwidth" → 'full'). */
const resolveEnum = <T extends string>(
  map: Record<T, number>,
  value: unknown,
  fallback: T,
  aliases: Record<string, T> = {}
): T => {
  if (value === null || value === undefined || value === '') return fallback;

  // Only numeric/string enum values are supported; anything else → fallback.
  // (Also keeps the String(value) calls below off non-primitives.)
  if (typeof value !== 'number' && typeof value !== 'string') return fallback;

  // numeric ID (number or numeric string)
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const id = Number(value);
    const byId = (Object.entries(map) as [T, number][]).find(
      ([, v]) => v === id
    );
    if (byId) return byId[0];
  }

  // string name (case-insensitive) matched against keys / aliases
  const name = String(value).toLowerCase();
  if (name in aliases) return aliases[name];
  const keys = Object.keys(map) as T[];
  return keys.includes(name as T) ? (name as T) : fallback;
};

/** Normalize a stored file (any of the metadata shapes the API returns) into
 *  LandingImage, or null when it has no usable path/name. */
const toLandingImageMeta = (file: any): LandingImage => {
  if (!file) return null;
  const filePath = file.filePath || '';
  const fileName = file.fileName || file.actualName || '';
  const key = file.key || '';
  if (!filePath && !fileName && !key) return null;
  return {
    fileName,
    internalName: file.internalName || '',
    filePath,
    key,
  };
};

/** Deserialize the backend payload (enum IDs + its field names) back into the
 *  UI config shape, so the customizer can be re-edited on the Edit flow.
 *
 *  `legacy` is the flat branding the customizer replaced — the admin API's
 *  `brandingInformation` or the public tenant-info object. Clients onboarded
 *  before the customizer have no landingPageBrandingInfo at all, so without
 *  this their real copy would silently load as the Shares.Vision defaults (and
 *  a save would then overwrite it). In that data titleOne holds the headline
 *  and titleTwo the caption — the slots the old landing page rendered them in.
 *  Note this is the reverse of the mapping buildPayload writes (caption ->
 *  titleOne), which is constrained by titleOne being the narrower column. */
export const fromLandingBackendPayload = (
  raw: any,
  legacy?: any
): LandingConfig => {
  const d = DEFAULT_LANDING_CONFIG;
  if (!raw && !legacy) return { ...d };
  raw = raw || {};
  legacy = legacy || {};

  // The backend serializer camel-cases acronym-heavy names unpredictably —
  // `secondaryCTAText` comes back as `secondaryCtatext`, `isPrimaryCTAEnabled`
  // as `isPrimaryCtaenabled` — so every field is resolved through a
  // case-insensitive view of the payload instead of exact key matches.
  const lowered: Record<string, unknown> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    lowered[key.toLowerCase()] = value;
  });
  const field = (name: string): any => lowered[name.toLowerCase()];

  // The GET response nests the banner as an `image` object; older payloads
  // carry flat imageFileName/imageFilePath fields. Accept both, then fall
  // back to the legacy branding banner.
  const rawImage = field('image');
  const image =
    toLandingImageMeta(typeof rawImage === 'object' ? rawImage : null) ||
    toLandingImageMeta({
      fileName: field('imageFileName'),
      internalName: field('imageInternalName'),
      filePath: field('imageFilePath'),
      key: field('imageKey') || field('key'),
    }) ||
    toLandingImageMeta(legacy.bannerImage);

  // `||` rather than `??` throughout the text fields: the API returns '' for
  // unset copy, which should fall through to the next source, not win.
  return {
    caption: field('caption') || legacy.titleTwo || d.caption,
    headline: field('headline') || legacy.titleOne || d.headline,
    paragraph: field('paragraph') || legacy.platformSummary || d.paragraph,
    textAlign: resolveEnum(
      TEXT_ALIGNMENT_ID,
      field('textAlignmentId') ?? field('textAlignment'),
      d.textAlign
    ),
    captionColor: field('captionColor') ?? d.captionColor,
    headlineColor: field('headlineColor') ?? d.headlineColor,
    paragraphColor: field('paragraphColor') ?? d.paragraphColor,
    primaryEnabled: field('isPrimaryCTAEnabled') ?? d.primaryEnabled,
    // `||`: '' should offer the default label again, not an empty button.
    primaryLabel: field('primaryCTAText') || d.primaryLabel,
    primaryBtnColor: field('primaryCTAButtonColor') ?? d.primaryBtnColor,
    secondaryEnabled: field('isSecondaryCTAEnabled') ?? d.secondaryEnabled,
    // `||`: a disabled CTA is stored with '' text, so re-ticking the checkbox
    // should offer the default label again, not an empty button.
    secondaryLabel: field('secondaryCTAText') || d.secondaryLabel,
    secondaryBtnColor: field('secondaryCTAButtonColor') ?? d.secondaryBtnColor,
    secondaryUrl: field('secondaryCTAUrl') ?? d.secondaryUrl,
    boxEnabled: field('isContentBoxBackgroundEnabled') ?? d.boxEnabled,
    // A blank string from the server is treated as "unset", not as a colour —
    // `??` would keep it and the enabled box would post an empty colour back.
    boxColor: field('contentBoxBackgroundColor')?.trim() || d.boxColor,
    boxEffect: resolveEnum(
      CONTENT_BOX_EFFECT_ID,
      field('contentBoxEffectId') ?? field('contentBoxEffect'),
      d.boxEffect
    ),
    boxOpacity: field('contentBoxOpacity') ?? d.boxOpacity,
    boxShadow: field('contentBoxShadow') ?? d.boxShadow,
    boxBlur: field('contentBoxBlur') ?? d.boxBlur,
    image,
    imageUrl:
      (typeof rawImage === 'string' && rawImage) ||
      (image && 'filePath' in image && image.filePath) ||
      field('imageFilePath') ||
      legacy.bannerFilePath ||
      d.imageUrl,
    imageOverlay: field('isImageOverlayEnabled') ?? d.imageOverlay,
    imageOverlayColor: field('imageOverlayColor') ?? d.imageOverlayColor,
    imageOverlayOpacity: field('imageOverlayOpacity') ?? d.imageOverlayOpacity,
    imagePosition: resolveEnum(
      IMAGE_POSITION_ID,
      field('layoutImagePositionId') ?? field('layoutImagePosition'),
      d.imagePosition,
      { fullwidth: 'full' }
    ),
    contentPosition: resolveEnum(
      CONTENT_POSITION_ID,
      field('layoutContentPositionId') ?? field('layoutContentPosition'),
      d.contentPosition
    ),
    borderRadius: field('layoutBorderRadius') ?? d.borderRadius,
    contentPadding: field('layoutContentPadding') ?? d.contentPadding,
    contentWidth: field('layoutContentWidth') ?? d.contentWidth,
  };
};

/** Build the UI config from the public tenant-info response. Prefers the
 *  `landingPageBrandingInfo` object; falls back to the flat tenant branding
 *  fields (titleOne/titleTwo/platformSummary/bannerFilePath) that the response
 *  also carries, which is exactly the `legacy` contract above — so the public
 *  page and the admin customizer resolve legacy copy through one mapping. */
export const fromTenantLandingInfo = (info: any): LandingConfig =>
  fromLandingBackendPayload(info?.landingPageBrandingInfo, info);

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Secondary CTA URL rule, shared by the customizer's Done button and the
 *  onboarding Yup schema so the two can't drift apart. Returns the error
 *  message, or undefined when valid. The backend rejects an enabled secondary
 *  CTA without a URL, and the landing page is public, so only https targets
 *  are accepted. Ignored entirely while the CTA is off — a stale value behind
 *  an unticked checkbox must not block anything (buildPayload drops it). */
export const secondaryUrlError = (
  enabled: boolean,
  url: string | null | undefined
): string | undefined => {
  if (!enabled) return undefined;
  const trimmed = url?.trim();
  if (!trimmed) return 'Secondary CTA URL is required';
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'https:' && parsed.hostname) return undefined;
  } catch {
    // not parseable as a URL — fall through to the error
  }
  return 'Enter a valid URL starting with https://';
};

export const hexToRgba = (hex: string, alpha: number) => {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map(c => c + c)
          .join('')
      : h;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Resolve the best preview src: a freshly-picked File, else metadata path,
 *  else the stored preview URL. */
export const getLandingImageSrc = (c: LandingConfig): string => {
  if (c.image instanceof File) return c.imageUrl;
  if (c.image && 'filePath' in c.image && c.image.filePath)
    return c.image.filePath;
  return c.imageUrl;
};
